import { memo, ReactNode, RefObject } from 'react';
import { playCrewRandomVOSample } from '@wg/web2clientapi/sound';
import { items, PresentationStyles } from '@wg/wows-entities/const';
import { SlotFunction } from '@wg/wows-entities/types/registerElement';
import { IVCrew, IVDoll, IVEnsign, IVPermoflages, IVVehicle } from '@wg/wows-entities/types/vortex';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import { Message } from '@wg/wows-react-uikit';
import classNames from 'classnames';
import ReactDOM from 'react-dom/client';
import { shallowEqual, useSelector } from 'react-redux';

import { isAuthorizedUser } from '~/config/user';
import { INVENTORY_KEY_BY_CATEGORY } from '~/helpers/consts';
import { t } from '~/helpers/localization';
import { isInGameBrowser } from '~/helpers/utils';
import { ICategories, IInventoryList, IInventoryState, IShipdestructionItem } from '~/interfaces';
import { Item as ItemType } from '~/interfaces';
import { CREWS_CATEGORY, DOLLS_CATEGORY, ENSIGNS_CATEGORY, PERMOFLAGES_CATEGORY, SHIPDESTRUCTION_CATEGORY, VEHICLE_CATEGORY } from '~/interfaces';
import { State } from '~/Reducers';
import { Crew, Doll, Permoflages } from '~/vortex';

import LazyElement from '../LazyElement/LazyElement';

import styles from './Item.scss';

interface IItem {
    category: ICategories;
    item: ItemType;
    parentRef: RefObject<HTMLDivElement>;
}

const isItemOwned = (item: ItemType, category: ICategories, inventory: IInventoryState): boolean => {
    if (!isAuthorizedUser()) {
        return false;
    }

    const inventoryKey = INVENTORY_KEY_BY_CATEGORY[category] as IInventoryList;
    const items = (inventory[inventoryKey] as string[]) || [];

    return items.includes(item.id);
};

interface IState {
    inventory: IInventoryState;
}

const stateSelector = (state: State): IState => {
    return {
        inventory: state.ReducerApp.inventory,
    };
};

const nodesMap = new WeakMap<HTMLElement, ReactDOM.Root>();

const Item = ({ category, item, parentRef }: IItem) => {
    const appState = useSelector<State, IState>(stateSelector, shallowEqual);
    let type = null;
    let shipId = null;
    const crewId: number | null = null;
    const isOwned = isItemOwned(item, category, appState.inventory);
    const isUser = isAuthorizedUser();
    let itemData;

    const tooltipContentAfterHeader = isUser
        ? [
              <Message type={isOwned ? 'done' : 'error'} style={null} key={`message_block_${item.id}`}>
                  {isOwned ? t('Получено') : t('Не получено')}
              </Message>,
          ]
        : [];

    const tooltipContentBeforeHeader: ReactNode[] = [];

    if (category === CREWS_CATEGORY) {
        type = items.CREWS;
        itemData = item as unknown as IVCrew;

        if (itemData.nation?.icons?.tiny) {
            tooltipContentBeforeHeader.unshift(
                <div className={styles.nationWrapper} key={`nation_block_${item.id}`}>
                    <img src={itemData.nation?.icons?.tiny} className={styles.nationIcon} />
                    <span>{itemData.nation.title}</span>
                </div>,
            );
        }
    } else if (category === DOLLS_CATEGORY) {
        type = (item as Doll).type;
        if (type !== items.BACKGROUND) {
            itemData = item as IVDoll;
        }
    } else if (category === ENSIGNS_CATEGORY) {
        type = items.ENSIGNS;
        itemData = item as IVEnsign;
    } else if (category === VEHICLE_CATEGORY) {
        type = items.VEHICLES;
        itemData = item as IVVehicle;
    } else if (category === PERMOFLAGES_CATEGORY) {
        const camo = item as Permoflages;
        itemData = item as IVPermoflages;
        const camoVehicle = camo.vehicles?.[0];
        type = items.PERMOFLAGES;
        if (camoVehicle) {
            shipId = camoVehicle.vehicle.id;
        }
    } else if (category === SHIPDESTRUCTION_CATEGORY) {
        type = items.SHIP_DESTRUCTION;
        itemData = item as IShipdestructionItem;
    }

    const classNameItem = classNames(styles.item, {
        [styles.notOwn]: isAuthorizedUser() ? !isOwned : false,
    });

    const isVoicePreviewEnabled = isInGameBrowser && type === items.CREWS && !!+item.id && (item as Crew).hasSampleVo;

    const previewCrewVoice = () => {
        if (!isVoicePreviewEnabled) {
            return;
        }
        const crewId = +item.id;
        playCrewRandomVOSample(crewId);
    };

    const customisationCallbacks: {
        afterHeaderTooltipSlot?: SlotFunction;
        beforeFooterTooltipSlot?: SlotFunction;
    } = {};

    if (!!tooltipContentAfterHeader.length) {
        customisationCallbacks['afterHeaderTooltipSlot'] = (node) => {
            if (!nodesMap.has(node)) {
                nodesMap.set(node, ReactDOM.createRoot(node));
            }
            nodesMap.get(node)?.render(tooltipContentAfterHeader);
        };
    }

    if (!!tooltipContentBeforeHeader.length) {
        customisationCallbacks['beforeFooterTooltipSlot'] = (node) => {
            if (!nodesMap.has(node)) {
                nodesMap.set(node, ReactDOM.createRoot(node));
            }
            nodesMap.get(node)?.render(tooltipContentBeforeHeader);
        };
    }

    return (
        <LazyElement className={classNameItem} parentRef={parentRef}>
            <WoWSEntity
                type={type}
                id={item.id}
                itemData={itemData}
                presentation={{
                    style: PresentationStyles.CARD,
                    withTooltip: true,
                    isAvailableCrewVoicePrevew: isVoicePreviewEnabled,
                    renderWithoutFade: true,
                }}
                customisation={{
                    shipId,
                    crewId,
                    ...customisationCallbacks,
                }}
                onClick={{
                    onCrewVoicePreview: previewCrewVoice,
                }}
            />
        </LazyElement>
    );
};

export default memo(Item);
